import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  DuplicateLeadSourceLabelError,
  useCreateLeadSource,
  useSetLeadSourceRetired,
  useLeadSources,
  useUpdateLeadSource,
  type LeadSource,
} from '@/routes/leadSources/useLeadSources';

/**
 * The account's lead-source catalog (M1-28) — "per-account managed list",
 * verbatim from the AC.
 *
 * <b>It lives at `/customers/lead-sources`, reached from the customers list.</b>
 * `/settings` was not an option for the reason M1-38 records: it is gated on
 * `account.manage`, which the M0-22 matrix gives the Owner ALONE, and this
 * vocabulary has to be editable by whoever books the calls. The route is
 * `customers.manage`, matching the API, so a Dispatcher reaches it — the same
 * person who fills the field on the create form.
 *
 * Structurally this is `SkillCatalog` (M1-38) with the nouns changed; the two
 * screens are the same interaction over the same entity shape. They are kept
 * as separate components rather than one generic catalog screen because the
 * only thing they would share is the markup, and the strings, routes,
 * permissions and query keys all differ — a "CatalogScreen<T>" would take four
 * props to say what two plain components say by existing.
 */
export function LeadSourceCatalog() {
  const { t } = useTranslation();
  const sources = useLeadSources(true);
  const create = useCreateLeadSource();

  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');

  function addLeadSource() {
    const label = newLabel.trim();
    if (!label) {
      return;
    }
    setError('');
    create.mutate(label, {
      onSuccess: () => setNewLabel(''),
      onError: (err) =>
        setError(
          err instanceof DuplicateLeadSourceLabelError
            ? err.message
            : t('leadSourceCatalog.addFailed'),
        ),
    });
  }

  return (
    <section className="space-y-4">
      <div>
        <Link to="/customers" className="text-sm underline underline-offset-2">
          {t('leadSourceCatalog.backToCustomers')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          {t('leadSourceCatalog.heading')}
        </h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('leadSourceCatalog.description')}
        </p>
      </div>

      {sources.isPending ? (
        <LoadingState label={t('leadSourceCatalog.loading')} />
      ) : sources.isError ? (
        <ErrorState
          title={t('leadSourceCatalog.loadErrorTitle')}
          onRetry={() => void sources.refetch()}
        />
      ) : (
        <div className="space-y-3 rounded-lg border p-4">
          {sources.data.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {t('leadSourceCatalog.empty')}
            </p>
          ) : (
            <ul className="space-y-1" data-testid="lead-source-catalog-list">
              {/* sortOrder round-trips as a string as often as a number — the
                  generated client types int32 as `number | string`, the fourth
                  field in this codebase to need the coercion (latitude,
                  longitude, sortOrder, and the licence counts). */}
              {[...sources.data]
                .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
                .map((source) => (
                  <LeadSourceRow key={source.id} source={source} />
                ))}
            </ul>
          )}

          <div className="flex items-end gap-2 border-t border-[var(--color-border)] pt-3">
            <label className="block flex-1 text-sm">
              <span className="mb-1 block font-medium">{t('leadSourceCatalog.newLabel')}</span>
              <input
                className={FIELD_CLASS}
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
              />
            </label>
            <Button
              type="button"
              size="sm"
              disabled={!newLabel.trim() || create.isPending}
              onClick={addLeadSource}
            >
              {t('leadSourceCatalog.add')}
            </Button>
          </div>

          {error ? (
            <p role="alert" className="text-sm text-[var(--color-destructive)]">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/**
 * One catalog row — rename inline, retire/restore. Retiring is never a delete:
 * assignments already reference this entry (api/CONVENTIONS.md's M1-38
 * section), so the API offers no delete for this screen to call.
 */
function LeadSourceRow({ source }: { source: LeadSource }) {
  const { t } = useTranslation();
  const update = useUpdateLeadSource();
  const setRetired = useSetLeadSourceRetired();
  const [label, setLabel] = useState(source.label);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Moves focus into the field once it appears, without the a11y pitfalls of
  // the declarative `autoFocus` prop that `jsx-a11y/no-autofocus` rejects —
  // this only runs in reaction to the person's own "Rename" click.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function save() {
    const trimmed = label.trim();
    if (!trimmed || trimmed === source.label) {
      setEditing(false);
      setLabel(source.label);
      return;
    }
    setError('');
    update.mutate(
      { id: source.id, label: trimmed, sortOrder: Number(source.sortOrder) },
      {
        onSuccess: () => setEditing(false),
        onError: (err) =>
          setError(
            err instanceof DuplicateLeadSourceLabelError
              ? err.message
              : t('leadSourceCatalog.renameFailed'),
          ),
      },
    );
  }

  return (
    <li
      data-testid="lead-source-catalog-row"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
    >
      {editing ? (
        <span className="flex flex-1 items-center gap-2">
          <label htmlFor={`lead-source-label-${source.id}`} className="sr-only">
            {t('leadSourceCatalog.renameLabelFor', { label: source.label })}
          </label>
          <input
            id={`lead-source-label-${source.id}`}
            ref={inputRef}
            className={FIELD_CLASS}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                save();
              }
              if (event.key === 'Escape') {
                setEditing(false);
                setError('');
                setLabel(source.label);
              }
            }}
          />
          <button type="button" className="text-xs underline underline-offset-2" onClick={save}>
            {t('common.save')}
          </button>
        </span>
      ) : (
        <span className="flex flex-1 flex-wrap items-center gap-2">
          <span
            className={source.isActive ? '' : 'text-[var(--color-muted-foreground)] line-through'}
          >
            {source.label}
          </span>
          {!source.isActive ? (
            <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
              {t('leadSourceCatalog.retiredChip')}
            </span>
          ) : null}
        </span>
      )}

      <span className="flex items-center gap-3">
        {!editing ? (
          <button
            type="button"
            className="text-xs underline underline-offset-2"
            onClick={() => setEditing(true)}
          >
            {t('leadSourceCatalog.rename')}
          </button>
        ) : null}
        <button
          type="button"
          className="text-xs underline underline-offset-2"
          disabled={setRetired.isPending}
          onClick={() => setRetired.mutate({ id: source.id, retired: source.isActive })}
        >
          {source.isActive ? t('leadSourceCatalog.retire') : t('leadSourceCatalog.restore')}
        </button>
      </span>

      {error ? (
        <p role="alert" className="w-full text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </li>
  );
}
